// components/sticker-picker/StickerPicker.jsx
import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HiMagnifyingGlass,
  HiXMark,
} from "react-icons/hi2";

import {
  STICKERS,
  STICKER_KEYS,
  STICKER_CATEGORIES,
} from "../stickerData";

import "./sticker-picker.styles.scss";

export { STICKER_KEYS };

const categoryLabels = Object.fromEntries(
  STICKER_CATEGORIES.map((category) => [
    category.key,
    category.label,
  ]),
);

const StickerPreview = memo(function StickerPreview({
  stickerKey,
  renderSticker,
}) {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      return;
    }

    if (
      typeof IntersectionObserver === "undefined"
    ) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setVisible(true);
        observer.disconnect();
      },
      {
        rootMargin: "180px",
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [visible]);

  return (
    <div
      ref={containerRef}
      className="stickerPicker__preview"
    >
      {visible &&
        (renderSticker ? (
          renderSticker(stickerKey)
        ) : (
          <span>{stickerKey}</span>
        ))}
    </div>
  );
});

export default function StickerPicker({
  onSelect,
  renderSticker,
}) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] =
    useState(STICKER_CATEGORIES[0].key);

  const stickers = useMemo(() => {
    const allStickers = STICKER_KEYS.map(
      (key) => STICKERS[key],
    );

    const clean = query.trim().toLowerCase();

    if (clean) {
      return allStickers.filter((sticker) => {
        const searchableText = [
          sticker.key,
          sticker.label,
          categoryLabels[sticker.category],
          ...(sticker.keywords || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(clean);
      });
    }

    return allStickers.filter(
      (sticker) =>
        sticker.category === activeCategory,
    );
  }, [query, activeCategory]);

  return (
    <div className="stickerPicker">
      <div className="stickerPicker__searchRow">
        <HiMagnifyingGlass />

        <input
          value={query}
          onChange={(event) =>
            setQuery(event.target.value)
          }
          placeholder="Search stickers"
          aria-label="Search stickers"
        />

        {!!query && (
          <button
            type="button"
            className="stickerPicker__clear"
            onClick={() => setQuery("")}
            aria-label="Clear sticker search"
          >
            <HiXMark />
          </button>
        )}
      </div>

      <div className="stickerPicker__categories">
        {STICKER_CATEGORIES.map((category) => {
          const active =
            category.key === activeCategory;

          return (
            <button
              type="button"
              key={category.key}
              className={`stickerPicker__category ${
                active
                  ? "stickerPicker__category--active"
                  : ""
              }`}
              onClick={() =>
                setActiveCategory(category.key)
              }
            >
              {category.label}
            </button>
          );
        })}
      </div>

      <div className="stickerPicker__grid">
        {stickers.map((sticker) => (
          <button
            type="button"
            key={sticker.key}
            className="stickerPicker__item"
            onClick={() =>
              onSelect?.(sticker.key)
            }
            title={sticker.label}
          >
            <StickerPreview
              stickerKey={sticker.key}
              renderSticker={renderSticker}
            />

            <small>{sticker.label}</small>
          </button>
        ))}

        {stickers.length === 0 && (
          <div className="stickerPicker__empty">
            No stickers found.
          </div>
        )}
      </div>
    </div>
  );
}